
import { useEffect, useRef, useState } from 'react';
import NeuralNetworkBg from '../../../components/feature/NeuralNetworkBg';

const issues = [
  {
    title: 'Flattened drive',
    icon: 'ri-battery-low-line',
    desc: 'Your motivation system has been hijacked by artificial rewards.',
  },
  {
    title: 'Volatile regulation',
    icon: 'ri-emotion-unhappy-line',
    desc: 'Emotional swings driven by constant dopamine spikes and crashes.',
  },
  {
    title: 'Cognitive fatigue',
    icon: 'ri-brain-line',
    desc: 'Mental exhaustion from processing thousands of micro-decisions daily.',
  },
  {
    title: 'Looped behavior',
    icon: 'ri-loop-left-line',
    desc: 'Compulsive patterns reinforced by algorithmic conditioning.',
  },
  {
    title: 'System overload',
    icon: 'ri-flashlight-line',
    desc: 'Your nervous system stuck in chronic fight-or-flight activation.',
  },
  {
    title: 'Loss of signal',
    icon: 'ri-signal-wifi-off-line',
    desc: 'Disconnection from your own body, intuition, and inner clarity.',
  },
];

export default function Problem() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-28 lg:py-36 px-6 lg:px-12 bg-white overflow-hidden"
    >
      {/* Subtle neural pathways in background */}
      <NeuralNetworkBg
        color="212,120,158"
        nodeCount={25}
        opacity={0.08}
        speed={0.4}
        variant="light"
      />

      <NeuralNetworkBg
        color="154,123,212"
        nodeCount={15}
        opacity={0.05}
        speed={0.3}
        variant="light"
      />

      {/* Soft gradient accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-pink/15 to-transparent"></div>

      <div className="relative z-10 max-w-6xl mx-auto">
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-50/80 border border-red-100/60 mb-8">
            <div className="w-2 h-2 rounded-full bg-red-300 animate-pulse"></div>
            <span className="text-xs font-semibold text-red-400 uppercase tracking-widest">
              The Problem
            </span>
          </div>

          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-8 tracking-tight leading-tight">
            You are living inside a<br />
            <span className="gradient-text">neurological experiment.</span>
          </h2>

          <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Your brain is being trained every second. Infinite scrolling. Constant
            stimulation. Algorithmic reinforcement. This is not passive
            consumption.{' '}
            <strong className="text-gray-600">It is a negative wiring that steals your health &amp; happiness and leaves you overloaded and flatlined.</strong>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {issues.map((issue, index) => (
            <div
              key={index}
              className={`group relative p-8 rounded-2xl bg-gradient-to-br from-gray-50/80 to-white border border-gray-100/80 hover:border-brand-pink/20 transition-all duration-500 hover:shadow-xl hover:shadow-brand-pink/8 cursor-pointer ${
                visible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-orange/3 via-brand-pink/3 to-brand-violet/3 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="relative">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-brand-pink/8 to-brand-violet/8 mb-5 group-hover:scale-110 transition-transform duration-500">
                  <i className={`${issue.icon} text-xl text-brand-pink`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 tracking-tight">
                  {issue.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">{issue.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
